import { z } from "zod";
import {
  dateSchema,
  timeSchema,
  monthSchema,
  firestoreTimestampSchema,
  overrideTypeEnum,
  slotStateEnum,
  blockReasonEnum,
} from "./common.schema";
import { siteInspectionSchema } from "./site-inspection.schema";

// ============================================
// TIME SLOT SCHEMA
// ============================================

export const timeSlotSchema = z.object({
  dayOfWeek: z.number().int().min(0).max(6), // 0 = Sunday, 6 = Saturday
  startTime: timeSchema,
  endTime: timeSchema,
});

export type TimeSlot = z.infer<typeof timeSlotSchema>;

// ============================================
// DOCTOR WEEKLY AVAILABILITY ENTITY
// ============================================

export const doctorWeeklyAvailabilitySchema = z.object({
  id: z.string().optional(),
  doctorId: z.string(),
  doctorName: z.string(), // Denormalized
  slots: z.array(timeSlotSchema),
  isActive: z.boolean(),
  updatedBy: z.string(),
  createdAt: firestoreTimestampSchema.optional(),
  updatedAt: firestoreTimestampSchema.optional(),
});

export type DoctorWeeklyAvailability = z.infer<
  typeof doctorWeeklyAvailabilitySchema
>;

// ============================================
// UPSERT WEEKLY AVAILABILITY DTO
// ============================================

export const upsertWeeklyAvailabilitySchema = z.object({
  slots: z.array(timeSlotSchema),
  isActive: z.boolean().optional(),
});

export type UpsertWeeklyAvailability = z.infer<
  typeof upsertWeeklyAvailabilitySchema
>;

// ============================================
// AVAILABILITY OVERRIDE ENTITY
// ============================================

export const availabilityOverrideSchema = z.object({
  id: z.string().optional(),
  doctorId: z.string(),
  date: dateSchema,
  type: overrideTypeEnum,
  startTime: timeSchema.optional(), // Optional for full-day blocks
  endTime: timeSchema.optional(),
  reason: z.string().optional(),
  // Extra override location field
  location: z.string().optional(),
  // Extra override reason (for extra type overrides)
  extraReason: z.enum(["generic", "site_inspection"]).optional(),
  // Site inspection fields
  siteInspectionId: z.string().optional(),
  siteInspectionDetails: siteInspectionSchema.optional(),
  // Company visit fields
  blockReason: blockReasonEnum.optional(),
  companyId: z.string().optional(),
  companyName: z.string().optional(),
  companyAddress: z.string().optional(),
  patientIds: z.array(z.string()).optional(),
  patientNames: z.array(z.string()).optional(),
  createdBy: z.string(),
  createdAt: firestoreTimestampSchema.optional(),
  updatedAt: firestoreTimestampSchema.optional(),
});

export type AvailabilityOverride = z.infer<typeof availabilityOverrideSchema>;

// ============================================
// CREATE OVERRIDE DTO
// ============================================

export const createOverrideSchema = z
  .object({
    date: dateSchema,
    type: overrideTypeEnum,
    startTime: timeSchema.optional(),
    endTime: timeSchema.optional(),
    reason: z.string().optional(),
    // Extra override location field
    location: z.string().optional(),
    // Extra override reason (for extra type overrides)
    extraReason: z.enum(["generic", "site_inspection"]).optional(),
    // Site inspection fields
    siteInspectionId: z.string().optional(),
    siteInspectionDetails: siteInspectionSchema.optional(),
    // Company visit fields
    blockReason: blockReasonEnum.optional(),
    companyId: z.string().optional(),
    companyName: z.string().optional(),
    companyAddress: z.string().optional(),
    patientIds: z.array(z.string()).optional(),
  })
  .refine(
    (data) => {
      if (data.blockReason === "company_visit" && !data.companyId) {
        return false;
      }
      return true;
    },
    {
      message: "Company is required for company visit blocks",
      path: ["companyId"],
    },
  );

export type CreateOverride = z.infer<typeof createOverrideSchema>;

// ============================================
// QUERY DTOs
// ============================================

export const queryOverridesSchema = z.object({
  startDate: dateSchema.optional(),
  endDate: dateSchema.optional(),
});

export type QueryOverrides = z.infer<typeof queryOverridesSchema>;

export const querySlotsSchema = z.object({
  date: dateSchema,
});

export type QuerySlots = z.infer<typeof querySlotsSchema>;

export const queryMultiDoctorSlotsSchema = z.object({
  date: dateSchema,
  doctorIds: z.string().optional(), // Comma-separated list
});

export type QueryMultiDoctorSlots = z.infer<typeof queryMultiDoctorSlotsSchema>;

export const queryMonthStatusSchema = z.object({
  month: monthSchema,
});

export type QueryMonthStatus = z.infer<typeof queryMonthStatusSchema>;

// ============================================
// CALCULATED SLOT (Response)
// ============================================

export const calculatedSlotSchema = z.object({
  time: timeSchema,
  endTime: timeSchema,
  durationMinutes: z.number().int().min(6).max(120),
  state: slotStateEnum,
  appointmentId: z.string().optional(),
  appointment: z
    .object({
      id: z.string(),
      patientName: z.string(),
      visitType: z.string(),
      status: z.string(),
    })
    .optional(),
  overrideId: z.string().optional(),
  blockReason: z.string().optional(),
  // Extra override location
  extraLocation: z.string().optional(),
  extraReason: z.enum(["generic", "site_inspection"]).optional(),
  // Extra override company (for generic extra with company association)
  extraCompanyId: z.string().optional(),
  extraCompanyName: z.string().optional(),
  extraCompanyAddress: z.string().optional(),
  extraNotes: z.string().optional(),
  // Company visit details
  companyVisit: z
    .object({
      companyId: z.string(),
      companyName: z.string(),
      companyAddress: z.string().optional(),
      patientIds: z.array(z.string()).optional(),
      patientNames: z.array(z.string()).optional(),
      notes: z.string().optional(),
    })
    .optional(),
  // Site inspection details
  siteInspection: z
    .object({
      siteInspectionId: z.string(),
      id: z.string().optional(),
      companyId: z.string().optional(),
      companyName: z.string().optional(),
      address: z.string().optional(),
      phone: z.string().optional(),
      notes: z.string().optional(),
    })
    .optional(),
  // Region details
  region: z
    .object({
      reason: z.string().optional(),
    })
    .optional(),
});

export type CalculatedSlot = z.infer<typeof calculatedSlotSchema>;

// ============================================
// DOCTOR SLOTS RESPONSE
// ============================================

export const doctorSlotsResponseSchema = z.object({
  doctorId: z.string(),
  doctorName: z.string(),
  doctorColor: z.string().optional(),
  date: dateSchema,
  hasAvailability: z.boolean(),
  slotDurationMinutes: z.number().int().min(6).max(120),
  slots: z.array(calculatedSlotSchema),
});

export type DoctorSlotsResponse = z.infer<typeof doctorSlotsResponseSchema>;

// ============================================
// NOTE CATEGORY
// ============================================

export const noteCategoryEnum = z.enum(["work", "sport"]);
export type NoteCategory = z.infer<typeof noteCategoryEnum>;

// ============================================
// UPSERT NOTE DTOs
// ============================================

export const upsertWeeklyNoteSchema = z.object({
  weekId: z.string().min(1, "Week ID is required"), // YYYY-WW format
  category: noteCategoryEnum,
  content: z.string().min(1, "Content is required"),
});

export type UpsertWeeklyNote = z.infer<typeof upsertWeeklyNoteSchema>;

export const weeklyNoteSchema = upsertWeeklyNoteSchema.extend({
  id: z.string().optional(),
  updatedBy: z.string(),
  createdAt: firestoreTimestampSchema.optional(),
  updatedAt: firestoreTimestampSchema.optional(),
});

export type WeeklyNote = z.infer<typeof weeklyNoteSchema>;

export const upsertDailyNoteSchema = z.object({
  date: dateSchema,
  category: noteCategoryEnum,
  content: z.string().min(1, "Content is required"),
});

export const dailyNoteSchema = upsertDailyNoteSchema.extend({
  id: z.string().optional(),
  createdAt: firestoreTimestampSchema.optional(),
  updatedAt: firestoreTimestampSchema.optional(),
  category: noteCategoryEnum,
  updatedBy: z.string(),
  content: z.string(),
});

export type DailyNote = z.infer<typeof dailyNoteSchema>;

export type UpsertDailyNote = z.infer<typeof upsertDailyNoteSchema>;

// Legacy aliases for backward compatibility
export const upsertNoteSchema = upsertWeeklyNoteSchema;
export type UpsertNote = UpsertWeeklyNote;

// ============================================
// ADMIN LOG ENTITY
// ============================================

export const adminLogActionEnum = z.enum([
  "FORCE_BOOKING",
  "UPDATE_AVAILABILITY",
  "CREATE_OVERRIDE",
  "DELETE_OVERRIDE",
  "DELETE_APPOINTMENT",
  "UPDATE_APPOINTMENT",
  "CREATE_SLOT_DURATION_RULE",
  "UPDATE_SLOT_DURATION_RULE",
  "DELETE_SLOT_DURATION_RULE",
]);

export const adminLogTargetTypeEnum = z.enum([
  "appointment",
  "availability",
  "override",
  "slot_duration_rule",
]);

export const adminLogSchema = z.object({
  id: z.string().optional(),
  userId: z.string(),
  userEmail: z.string(),
  action: adminLogActionEnum,
  targetType: adminLogTargetTypeEnum,
  targetId: z.string(),
  details: z.record(z.unknown()),
  timestamp: firestoreTimestampSchema,
});

export type AdminLog = z.infer<typeof adminLogSchema>;

// ============================================
// WEEKLY PLANNING INTERFACES
// ============================================

/**
 * Weekly Slot - Aggregated appointments for a time slot
 */
export interface WeeklySlot {
  /**
   * Date in YYYY-MM-DD format
   */
  date: string;

  /**
   * Time in HH:mm format
   */
  time: string;

  /**
   * Number of work visits
   */
  workCount: number;

  /**
   * Number of sport visits
   */
  sportCount: number;

  /**
   * Total appointments
   */
  totalCount: number;

  /**
   * Whether any appointment is a withdrawal
   */
  hasWithdrawal: boolean;
}

/**
 * Doctor day detail - Aggregated appointments for a doctor on a specific day
 */
export interface DoctorDayDetail {
  /**
   * Doctor's unique identifier
   */
  doctorId: string;

  /**
   * Doctor's display name
   */
  doctorName: string;

  /**
   * Hex color code for UI visualization (e.g., "#FF5733")
   */
  doctorColor?: string;

  /**
   * Combined activities: "Visite", "Prelievi", or "Visite + Prelievi"
   */
  activities: string;

  /**
   * Time range calculated from appointments (e.g., "09:00 - 12:00")
   */
  timeRange: string;

  /**
   * Unique locations/sites where doctor has appointments
   */
  locations: string[];

  /**
   * Total number of appointments for this doctor on this day
   */
  appointmentCount: number;

  /**
   * Count of work visits (excluding withdrawals)
   */
  workVisitsCount: number;

  /**
   * Count of sport visits (excluding withdrawals)
   */
  sportVisitsCount: number;

  /**
   * List of exams with counts
   */
  exams: { id: string; name: string; count: number }[];

  /**
   * Period when prelievi (blood draws) are scheduled
   * 'mattino' = morning only, 'pomeriggio' = afternoon only, 'both' = morning and afternoon, undefined = no prelievi
   */
  prelieviPeriod?: "mattino" | "pomeriggio" | "both";
}

/**
 * Daily aggregated data for weekly planning
 */
export interface DailyAggregate {
  /**
   * Date in YYYY-MM-DD format
   */
  date: string;

  /**
   * Day of week (0 = Sunday, 6 = Saturday)
   */
  dayOfWeek: number;

  /**
   * Time slots for this day
   */
  slots: WeeklySlot[];

  /**
   * Total appointments for the day
   */
  totalAppointments: number;

  /**
   * Total work visits
   */
  workVisits: number;

  /**
   * Total sport visits
   */
  sportVisits: number;

  /**
   * List of exams with counts
   */
  exams: { id: string; name: string; count: number }[];

  /**
   * Doctor details for this day
   */
  doctorDetails: DoctorDayDetail[];
}

/**
 * Weekly Plan - Complete weekly view with aggregated data
 */
export interface WeeklyPlan {
  /**
   * Week identifier
   */
  weekId: string;

  /**
   * Start date of the week
   */
  startDate: string;

  /**
   * End date of the week
   */
  endDate: string;

  /**
   * Daily aggregates for the week
   */
  days: DailyAggregate[];

  /**
   * Notes for the week
   */
  notes: {
    work?: WeeklyNote;
    sport?: WeeklyNote;
  };
}

// ============================================
// WEEKLY DASHBOARD INTERFACES (Phase 6)
// ============================================

/**
 * Doctor assignment detail for weekly dashboard
 */
export interface DoctorAssignmentDetail {
  id: string;
  displayName: string;
  color?: string;
  workingHours: string; // e.g. "09:00-13:00"
  overrideLabel?: string;
  overrideHours?: string;
  overrideLocation?: string;
  appointmentsCount: number;
  examsCount: number;
}

/**
 * Clinic assignment with detailed doctor info for dashboard
 */
export interface ClinicAssignmentWithDetails {
  clinic: {
    id: string;
    name: string;
    code?: string;
    color?: string;
  };
  doctors: DoctorAssignmentDetail[];
  totalAppointments: number;
}

/**
 * Doctor available but not assigned to any clinic on a given day
 */
export interface UnassignedDoctorDetail {
  id: string;
  displayName: string;
  color?: string;
  workingHours: string;
  overrideLabel?: string;
  overrideHours?: string;
  overrideLocation?: string;
}

/**
 * Day data for the weekly dashboard
 */
export interface WeeklyDashboardDay {
  date: string;
  dayOfWeek: number;
  stats: {
    total: number;
    workVisits: number;
    sportVisits: number;
    exams: Array<{ id: string; name: string; count: number }>;
  };
  clinicAssignments: ClinicAssignmentWithDetails[];
  unassignedDoctors: UnassignedDoctorDetail[];
}

/**
 * Doctor schedule entry for weekly summary
 */
export interface DoctorScheduleEntry {
  date: string;
  clinicId?: string;
  clinicName?: string;
  workingHours?: string;
  overrideLabel?: string;
  overrideHours?: string;
  overrideLocation?: string;
  appointmentsCount: number;
  examsCount: number;
}

/**
 * Doctor weekly summary for dashboard
 */
export interface DoctorWeeklySummary {
  doctor: {
    id: string;
    displayName: string;
    color?: string;
  };
  weeklySchedule: DoctorScheduleEntry[];
  totalAppointments: number;
  totalExams: number;
}

/**
 * Complete weekly dashboard data
 */
export interface WeeklyDashboardData {
  weekId: string;
  startDate: string;
  endDate: string;
  summary: {
    totalAppointments: number;
    workVisits: number;
    sportVisits: number;
    examsCount: number;
    activeClinics: number;
    activeDoctors: number;
  };
  days: WeeklyDashboardDay[];
  doctorsSummary: DoctorWeeklySummary[];
  notes: {
    work?: WeeklyNote;
    sport?: WeeklyNote;
  };
}
